import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { compose } from 'redux';
import { useDispatch } from 'react-redux';
import NotificationSystem from 'react-notification-system';
import { withRouter, Prompt } from 'react-router-dom';
import Validator from 'components/Validator/Validator';
import { format } from 'date-fns';
import get from 'lodash/get';

import Header from 'components/Header';
import { Grid, Column } from 'components/Layout';
import SettlementTable from 'components/SettlementTable';
import FormGroup from 'components/Form/FormGroup';
import Input from 'components/Form/Input';
import { Button } from 'components/Button';
import { parseUrlParams } from 'utils/parseUrlParams';
import { formatCurrency } from 'utils/formatCurrency';

import {
  nextAction,
} from 'actions/workflow';

import { getSettlements } from 'actions/settlement';

import {
  getApplications,
  getMerchantDetail,
} from 'actions/application';

import Certificate from 'assets/icons/certificate.svg';
import schema from './schema';
import style from './style.scss';

const CertificateSettlement = (props) => {
  const { validator: { values, errors } } = props;
  const dispatch = useDispatch();
  const columns = [
    { name: 'Original Creditor' },
    { name: 'Current Creditor' },
    { name: 'Settlement ID' },
    { name: 'Account # (Last 4)' },
    { name: 'Original Account Balance' },
    { name: 'Final Account Balance' },
    { name: 'Pay-off Amount to Creditor' },
    { name: 'Estimated' },
    { name: 'Actual' },
    { name: 'Date of Settlement' },
    { name: 'Amount Due to DSC' },
    { name: 'FSL Fee' },
  ];
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const [lusaPercentage, setLusaPercentage] = useState(0);
  const [clientId, setClientId] = useState('');
  const [editedRows, setEditedRows] = useState([]);
  const [dscFeePercentage, setDscFeePercentage] = useState(0);
  const [applicantName, setApplicantName] = useState(null);
  const [totalLoanAmount, setTotalLoanAmount] = useState(null);
  const [totalAmountUsed, setTotalAmountUsed] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [dscFees, setDscFees] = useState(null);
  const [cdaBalance, setCdaBalance] = useState(null);
  const notificationSystem = useRef(null);
  const remainingAmount = useMemo(() => (Number(totalLoanAmount) + Number(cdaBalance || 0)) - Number(totalAmountUsed), [totalLoanAmount, totalAmountUsed, cdaBalance]);

  const getAllSettlements = () => {
    const params = parseUrlParams(window.location.search);
    setIsLoading(true);
    dispatch(getSettlements({
      url: `/fsl-settlements/${params.applicationId}`,
      success: (response) => {
        setApplicantName(response.applicantName);
        setTotalLoanAmount(response.amountFinanced);
        setTotalAmountUsed(response.settlements.reduce((total, item) => Number(total) + Number(item.current.amount) + Number(item.dscFee), 0));
        setDscFees(response.settlements.reduce((total, item) => Number(total) + Number(item.dscFee), 0));
        setRows(response.settlements.map(item => ({
          creditorName: item.creditorName,
          currentCreditor: item.currentCreditor,
          settlementId: item.settlementId,
          creditAccountNumber: item.creditAccountNumber,
          accountBalance: item.accountBalance,
          finalAccountBalance: item.finalAccountBalance || 0,
          amount: item.current.amount,
          isActual: item.current.isActual,
          settlementDate: item.current.settlementDate,
          dscFee: item.dscFee,
          lusaFee: item.lusaFee,
          id: item.id,
          isCertificateOfSettlement: item.isCertificateOfSettlement || false,
        })));
        setIsLoading(false);
      },
      fail: (error) => { // eslint-disable-line
        setIsLoading(false);
      },
    }));
  };

  const getAllMerchantDetail = () => {
    const merchantId = localStorage.getItem('merchantId');
    if (merchantId) {
      dispatch(getMerchantDetail({
        url: `/merchants/${merchantId}/basic`,
        success: (res) => {
          setLusaPercentage((res.fee ? res.fee : 0) / 100);
          setDscFeePercentage(res.fee ? res.fee : 0);
        },
        fail: () => {},
      }));
    }
  };

  const getApplicationDetail = () => {
    const params = parseUrlParams(window.location.search);
    dispatch(getApplications({
      url: `/applications/?applicationId=${params.applicationId}`,
      success: (response) => {
        setCdaBalance(get(response, 'data.0.channel.attributes.customerDedicatedAccountBalance') || 0);
        setClientId(get(response, ['data', 0, 'channel', 'attributes', 'clientId']));
      },
      fail: (error) => { // eslint-disable-line
      },
    }));
  };

  useEffect(() => {
    getAllSettlements();
    getApplicationDetail();
    getAllMerchantDetail();
  }, []);

  useEffect(() => {
    if (isEdited) {
      window.onbeforeunload = () => true;
    } else {
      window.onbeforeunload = undefined;
    }
  }, [isEdited]);

  const getCurrentDate = () => format(new Date(), 'M/dd/yyyy');

  const handleInputChange = (event) => {
    const { validator: { onChangeHandler } } = props;
    onChangeHandler(event.target.name, event.target.value);
  };

  const handleUpdateRow = (id, row) => {
    const rowsClone = [...rows];
    if (editedRows.indexOf(id) === -1) {
      editedRows.push(id);
    }

    rowsClone[id] = {
      ...rows[id],
      ...row,
      lusaFee: formatCurrency(Number(row.dscFee || 0) * lusaPercentage, 2),
    };

    setRows(rowsClone);
    setIsEdited(true);
    setTotalAmountUsed(rows.reduce((total, item) => Number(total) + Number(item.amount) + Number(item.dscFee), 0));
    setDscFees(rows.reduce((total, item) => Number(total) + Number(item.dscFee), 0));
  };

  const handleSubmitButton = (e) => {
    e.preventDefault();
    const params = parseUrlParams(window.location.search);
    const { validator: { validate } } = props;
    if (validate(schema).isValid) {
      setIsLoading(true);

      const settlements = [];
      rows.forEach((item, index) => {
        if (editedRows.indexOf(index) !== -1) {
          settlements.push({ ...item, isCertificateOfSettlement: true });
        }
      });

      dispatch(nextAction({
        data: {
          settlements,
          signatureBy: {
            name: values.signature,
            date: getCurrentDate(),
          },
          generateDocument: true,
        },
        url: `/fsl-settlements/${params.applicationId}/settlements`,
        // eslint-disable-next-line
        success: (response) => {
          getAllSettlements();
          setIsEdited(false);
          setEditedRows([]);
        },
        // eslint-disable-next-line
        fail: (error) => {
          notificationSystem.current
          && notificationSystem.current.addNotification({
            message: get(error, 'data.message') || 'Submitting application form failes',
            level: 'error',
            position: 'tc',
          });
          setIsLoading(false);
        },
      }));
    }
  };

  return (
    <Fragment>
      <Prompt
        when={isEdited}
        message="You have not submitted the form, are you sure you want to leave?"
      />
      <Header />
      <NotificationSystem ref={notificationSystem} />
      <section className="container section">
        <div className="grid-container fluid borrowers-apply page-certificate">
          <div className="grid-container">
            <Grid>
              <Column className="shrink form-headline">
                <img src={Certificate} alt="certificate" className={cn('header-icon', style.headerLogo)} />
              </Column>
              <Column className="auto form-headline">
                <h2>Certificate of Settlement</h2>
                <p className="p-large">Below is your statement of settled debts.</p>
              </Column>
              <Column className="large-4 sidebar-apply sticky-container" data-sticky-container="">
                <div className="sticky" data-sticky="" data-top-anchor="Sidebar" data-btm-anchor="Footer" data-margin-top="7.5" data-resize="mn7cem-sticky" data-mutate="mn7cem-sticky" data-e="4uqwkw-e">
                  <div className="card">
                    <div className={style['card-header']}><h5>Client Information</h5></div>
                    <div className={cn('card-section', style['card-section'])}>
                      <Grid>
                        <Column>
                          <h6 className={style.heading6}>Applicant Name</h6>
                          <p>{applicantName}</p>
                        </Column>
                        <Column>
                          <h6 className={style.heading6}>CDA Balance</h6>
                          <p>${formatCurrency(cdaBalance, 2)}</p>
                        </Column>
                        <Column>
                          <h6 className={style.heading6}>Client ID</h6>
                          <p>{clientId || '-'}</p>
                        </Column>
                      </Grid>
                    </div>
                  </div>
                </div>
              </Column>
              <Column className="small-12">
                <div className="form-group">
                  <h4>Statement of Settled Debts</h4>
                  <Grid className="grid-margin-x" style={{ marginBottom: '50px' }}>
                    <Column className="large-12 small-12">
                      <SettlementTable
                        rows={rows}
                        columns={columns}
                        updateRow={handleUpdateRow}
                        isLoading={isLoading}
                        lusaPercentage={lusaPercentage}
                      />
                    </Column>
                  </Grid>
                  <Grid className={cn('grid-margin-x', style['settlement-details'])}>
                    <Column className="small-4 medium-4">
                      <h5 className={style.heading5}>Amount Financed</h5>
                      ${(totalLoanAmount && formatCurrency(totalLoanAmount, 2)) || ' -'}
                    </Column>
                    <Column className="small-4 medium-4">
                      <h5 className={style.heading5}>Total Amount Used</h5>
                      ${(totalAmountUsed && formatCurrency(totalAmountUsed, 2)) || ' -'}
                    </Column>
                    <Column className="small-4 medium-4">
                      <h5 className={style.heading5}>Remaining Amount</h5>
                      ${(totalLoanAmount && totalAmountUsed && formatCurrency(remainingAmount, 2)) || ' -'}
                    </Column>
                  </Grid>
                </div>
              </Column>
              { isEdited &&
                <Fragment>
                  <Column className="small-12">
                    <h5>By executing this Certification of Settlement</h5>
                    <div className={style.terms}>
                      <ol>
                        <li>You certify that the above listed information is true and accurate.</li>
                        <li>You certify that the fee you charged the client was collected post settlement and in accordance with all applicable state and federal laws and regulations.</li>
                        <li>You did not charge the client any fees in connection with debts that were settled by the client.</li>
                        <li>You certify that the fee was not charged in advance of the services to be performed.</li>
                        <li>You agree to pay LendingUSA {dscFeePercentage}% of the DSC fee within 3 business days of the date that the debt was settled.</li>
                        <li>You authorize LendingUSA to debit the Merchant Discount fee from your Merchant Bank Account that we have on file.</li>
                        <li>You certify that your Company has: (1) settled all of the debts listed above on behalf of the client; and (2) that the client did not independently settle any of the above listed debts.</li>
                        <li>You are duly authorized by the Company to make these representations and certifications on its behalf.</li>
                        <li>You understand that the falsification and/or misrepresentation of any of the above information could result in the suspension and/or immediate termination of your Participation in the Fresh Start Lending Program.</li>
                      </ol>
                    </div>
                  </Column>
                  <Column className="large-8 large-offset-2">
                    <FormGroup>
                      <h4>Your Signature</h4>
                      <Grid className="grid-margin-x">
                        <Column className="small-12 medium-9">
                          <Input
                            label="E-Signature (Type full name)"
                            name="signature"
                            value={values.signature}
                            onChange={handleInputChange}
                            isRequired
                            hasError={!!errors.signature}
                          />
                        </Column>
                        <Column className="small-12 medium-3">
                          <Input
                            label="Date"
                            name="date"
                            value={getCurrentDate()}
                            isDisabled
                            // hasError={!!errors['applicant.firstName']}
                          />
                        </Column>
                      </Grid>
                      <Button
                        className="button large arrow green w-100"
                        onClick={handleSubmitButton}
                        isLoading={isLoading}
                      >
                        Submit
                      </Button>
                    </FormGroup>
                  </Column>
                </Fragment>
              }
            </Grid>
          </div>
        </div>
      </section>
    </Fragment>
  );
};

CertificateSettlement.propTypes = {
  validator: PropTypes.object.isRequired,
};

CertificateSettlement.defaultProps = {
};

export default compose(
  Validator(schema),
  withRouter
)(CertificateSettlement);



// WEBPACK FOOTER //
// ./src/containers/pages/CertificateSettlement/index.js